import React, { useState, useEffect, useRef, Children } from 'react';
import * as S from './styles';
import { IProps, IState } from './types';
import { RootState } from 'store/types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { openSettingsPanelAction } from 'features/SettingsPanel/state/actions';
import { getOpenSettingsPanel } from 'features/SettingsPanel/state/selectors';
import RemoveIconWhite from 'common/icons/removeIconWhite';
import Switch from 'components/Switch'
import { Menu } from 'features/Menu/state/types';
import ConfigIcon from 'common/icons/configInfo';
import RadioButtonIcon from 'common/icons/radioButtonIcon';
import { EnumLMPackage } from 'features/Dealers/DealerSelect/state/types';
import { ListingUI } from 'features/Dealers/Listings/state/types';
import { editSelectedDealer, updateSelectedDealerLMPackage } from 'features/Dealers/DealerSelect/state/actions';
import { EngineLocation } from 'features/Dealers/DealerConfiguration/Departments/state/types';

const SettingsPanel: React.FC<IProps> = (props) => {

  const useOutsideAlerter = (ref: any) => {
    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (ref.current && !ref.current.contains(event.target)) {
          handleCloseSettingsPanel();
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const settingsPanelRef = useRef(null);
  useOutsideAlerter(settingsPanelRef);

  const dispatch = useDispatch();
  const openSettingsPanel = useSelector(getOpenSettingsPanel);

  const {
    user,
    dealerSelected,
    engineDealer,
    listing,
    locations,
    listings,
    preview,
    menuItems
  } = useSelector((rootState: RootState) => { 
      const listingsByDealerId = rootState.dealers.listing.listings.byDealerId;
      const selectedDealer = rootState.dealers.dealer.selectedDealer;
      let listings: ListingUI[] = []
      if (listingsByDealerId[selectedDealer?.id ?? ""]) {
          listings = listingsByDealerId[selectedDealer?.id ?? ""];
      }
      return {
        user: rootState.security.user,
        dealerSelected: rootState.dealers.dealer.selectedDealer,
        engineDealer: rootState.dealers.enginedealer.enginedealer,
        listing: rootState.dealers.listing,
        locations: rootState.dealers.departments.engineLocations,
        listings,
        preview: rootState.dealers.listing.currentListing.enginePreview.gmbPreview,
        menuItems: rootState.menu.items?.filter(item => item.settingsPanel === true)
      }
  }, shallowEqual);

  const packageSelected = dealerSelected?.lmPackage ?? 'BASIC';
  const [menuItemsState, setMenuItemsState] = useState<IState['item']>([]);
  const [option, setOption] = useState('');

  useEffect(() => {
    if (menuItems?.length && dealerSelected) {
      setMenuItemsState(menuItems.filter(item => {
        var value = true;
        if (packageSelected === "BASIC") value = item.internalName !== "location";
        return value;
      }).map(item => {return {
        editing: false,
        displayName: item.displayName,
        internalName: item.internalName,
        children: getChildren(item),
        description: getDescription(item)
      }}));
    }
  }, [JSON.stringify(menuItems), JSON.stringify(dealerSelected), openSettingsPanel]);

  const getChildren = (item: Menu) => {
    let children: any = undefined;
    if (item.children?.length > 0) {
      children = item.children.map(item => {return {displayName: item.displayName, internalName: item.internalName}});
    }
    return children;
  }

  const getDescription = (item: Menu) => {
    let description = "";
    if (item.internalName === "package") {
      const itemFiltered = item.children.filter(item => item.internalName.toUpperCase() === (packageSelected));
      if (itemFiltered.length > 0) description = itemFiltered[0].displayName;
    }
    if (item.internalName === "location") {
      const listings = listing.listings?.byDealerId[`${dealerSelected?.id ?? ''}`];
      if (listings?.length) {
        const listingSynced = listings.filter(listingItem => (listingItem.statusSync === "SYNCED" && listingItem.type === "PRIMARY"));
        if (listingSynced.length) {
          return `${listingSynced[0].idInSource}`;
        }
      }
    }
    return description;
  }

  const handleCloseSettingsPanel = () => {
    dispatch(openSettingsPanelAction(false));
  }

  const handleOpenSettingsPanel = () => {
    dispatch(openSettingsPanelAction(true));
  }

  const handleEdit = (index: number, value: boolean) => {
    if (props.isSynced) return;
    if (menuItemsState[index].internalName === 'package') setOption(packageSelected);
    setMenuItemsState([
      ...menuItemsState.slice(0, index),
      {...menuItemsState[index], editing: value},
      ...menuItemsState.slice(index + 1)
    ]);
  }

  const handleSave = (item: any, index: number) => {
    const descriptionOption = menuItemsState[index].children?.filter(item => item.internalName.toUpperCase() === option)[0].displayName;
    if(dealerSelected && descriptionOption) {
      dispatch(editSelectedDealer({...dealerSelected, lmPackage: option as EnumLMPackage}));
      dispatch(updateSelectedDealerLMPackage(dealerSelected.id, option));
      setMenuItemsState([
      ...menuItemsState.slice(0, index),
      {...menuItemsState[index], editing: false, description: descriptionOption},
      ...menuItemsState.slice(index + 1)
    ]);
    }
  }

  const handleSelectItem = (item: any, index: number) => {
    setOption(item.internalName?.toUpperCase());
  }

  const renderRadioButton = (item: any) => {
    let radioStyles: any = {fillColor: '#FFFFFF00', strokeColor: '#a5a5a5'};
    if (item.internalName.toUpperCase() === option) radioStyles = {fillColor: undefined, strokeColor: undefined}
    return <RadioButtonIcon {...radioStyles} />
  }

  const location = (idInSource: string): EngineLocation|undefined => {
    if (locations && locations.locations) {
      for (let i=0;i<locations?.locations.length;i++) {
        if (locations.locations[i].locationId === idInSource) {
          return locations.locations[i];
        }
      }
    }
    return undefined;
  }

  const locationBasic = (item: any, index: number) => {
    let listingsLocationsGMB: EngineLocation[] = [];
    for (let i=0;i<listings.length;i++) {
      if (listings[i].type === "PRIMARY" && listings[i].engineId === "GMB") {
        const locationPri: EngineLocation|undefined = location(listings[i].idInSource);
        if (locationPri) listingsLocationsGMB.push(locationPri);
        else listingsLocationsGMB.push({locationId: listings[i].idInSource, locationName: preview?.name??""});
      }
    } 
    for (let i=0;i<listings.length;i++) {
      if (listings[i].type !== "PRIMARY" && listings[i].engineId === "GMB") {
        const locationDep: EngineLocation|undefined = location(listings[i].idInSource);
        if (locationDep) listingsLocationsGMB.push(locationDep);
      }
    } 
    if (packageSelected === "BASIC" && listingsLocationsGMB && listingsLocationsGMB.length>0)
    {
      return listingsLocationsGMB.map((location, index2) => {
        return (
          <S.Option 
          key={index + index2} 
          hoverValue={'auto'}
          >
            <S.OptionTitle>
              {item.displayName}
            </S.OptionTitle>
            <S.OptionDescription>
              {
                (location.locationName ? location.locationName + ' - ' : '') + location.locationId 
              }
            </S.OptionDescription>
          </S.Option>
        )
      })
    }
  }

  return (
    <S.Root
      ref={settingsPanelRef}
      displayValue={dealerSelected === undefined || dealerSelected?.id === '1' || engineDealer === undefined || engineDealer.statusConected === "INACTIVE" || engineDealer.statusConected === "" || user?.role === "DealerUser" ? 'none' : 'auto'}
    >
      <S.Icon
        backgroundColorValue={openSettingsPanel ? '#4da0ff' : 'auto'}
        onClick={handleOpenSettingsPanel}
      >
        <ConfigIcon/>
      </S.Icon>   
      <S.Container 
        displayValue={openSettingsPanel ? 'auto' : 'none'}
      >
        <S.Title>
          Configuration
        </S.Title>
        <S.CloseIconContainer
          onClick={handleCloseSettingsPanel}
        >
          <RemoveIconWhite fillColor='#adadbf' />
        </S.CloseIconContainer>
        <S.SyncRow>
          <Switch checked={props.isSynced} onChange={props.handleChangeSync}/>
          <S.SyncText>
            Sync to Listings Manager
          </S.SyncText>
        </S.SyncRow>
        <S.OptionsContainer>
          {
            menuItemsState.map((item, index) => {
              return (
                item.internalName === 'locationBasic' ?
                  locationBasic(item, index)
                :
                <S.Option 
                  key={index} 
                  hoverValue={item.internalName === 'location' ? 'auto' : undefined}
                >
                  <S.ButtonContainer>
                    {
                      item.editing ?
                        <>
                          <S.OptionButton
                            onClick={() => handleEdit(index, false)}
                            colorValue={'#122442'}
                            borderColorValue={'#122442'}
                          >
                            Cancel
                          </S.OptionButton>
                          <S.OptionButton
                            onClick={() => handleSave(item.children, index)}
                          >
                            Save
                          </S.OptionButton>
                        </>
                      :
                        <S.OptionButton
                          onClick={() => handleEdit(index, true)}
                          displayValue={item.internalName === 'location' ? 'none' : 'auto'}
                        >
                          Edit
                          <S.InfoButton
                            displayValue={props.isSynced ? 'flex' : 'none'}
                          >
                            In order to change the package, first disable the toggle
                          </S.InfoButton>
                        </S.OptionButton>
                    }
                  </S.ButtonContainer>
                  <S.OptionTitle>
                    {item.displayName}
                  </S.OptionTitle>
                  <S.OptionDescription displayValue={item.editing ? 'none' : 'auto'} >
                    {item.description}
                  </S.OptionDescription>
                  {
                    (item.editing && item.children?.length) &&
                    <S.ListContainer>
                      {
                        item.children.map((item, index) => {return (
                          <S.ListItemContainer key={index} >
                            <S.ListItemRadioButton
                              onClick={() => handleSelectItem(item, index)}
                            >
                              {renderRadioButton(item)}
                            </S.ListItemRadioButton>
                            <S.ListItemDescription>
                              {item.displayName}
                            </S.ListItemDescription>
                          </S.ListItemContainer>
                        )})
                      }
                    </S.ListContainer>
                  }
                </S.Option>
              )
            })
          }
        </S.OptionsContainer>
      </S.Container>
    </S.Root>
  );
}

export default SettingsPanel;